import * as THREE from "three";
import { VOXEL_SIZE } from "./VoxelInstanceBuffer";
import { CharacterRenderer, type CharacterType } from "./assets/VoxelCharacters";
import type { VoxelAsset } from "./voxelTypes";

/**
 * The villagers who walk the authored village.
 *
 * Each one paces a fixed route and turns around at the ends, which is enough to make the place
 * look inhabited without any pathfinding. Two of them are special-cased: the miller works a
 * multi-stop route out of the mill and back, and the villager crossing the bridge has to ride
 * its arch rather than walk through it.
 */

/** Where a character is going, in voxel coordinates. */
export type CharacterPath = {
  type: CharacterType;
  start: { x: number; y: number; z: number };
  end: { x: number; y: number; z: number };
};

type Placed = { asset: VoxelAsset; x: number; y: number; z: number; rotation?: number };

/** Lifts a walker so its feet sit on top of the surface voxel rather than inside it. */
const FOOT_LIFT = 0.5 * VOXEL_SIZE + 0.05;

type Walker = {
  type: CharacterType;
  renderer: CharacterRenderer;
  speed: number;
  paused: boolean;
  pauseFor: number;
  /** Straight there-and-back walkers. */
  start?: THREE.Vector3;
  end?: THREE.Vector3;
  progress: number;
  direction: number;
  /** The miller's multi-stop route. */
  waypoints?: THREE.Vector3[];
  legIndex: number;
  legProgress: number;
};

export class VillagerCrowd {
  private walkers: Walker[] = [];

  constructor(scene: THREE.Scene, paths: CharacterPath[], cottages: Placed[], private bridge?: Placed) {
    const mill = cottages.find(
      (cottage) => /watermill|mill/i.test(cottage.asset.name) || cottage.asset.id === "cottage_3"
    );

    for (const path of paths) {
      const renderer = new CharacterRenderer(path.type);
      scene.add(renderer.group);

      if (path.type === "miller" && mill) {
        this.walkers.push(this.buildMiller(renderer, mill));
        continue;
      }

      const start = new THREE.Vector3(
        path.start.x * VOXEL_SIZE,
        (path.start.y + 0.5) * VOXEL_SIZE + 0.05,
        path.start.z * VOXEL_SIZE
      );
      const end = new THREE.Vector3(
        path.end.x * VOXEL_SIZE,
        (path.end.y + 0.5) * VOXEL_SIZE + 0.05,
        path.end.z * VOXEL_SIZE
      );
      renderer.group.position.copy(start);
      renderer.group.lookAt(end);

      this.walkers.push({
        type: path.type,
        renderer,
        speed: 0.8,
        paused: false,
        pauseFor: 0,
        start,
        end,
        progress: 0,
        direction: 1,
        legIndex: 0,
        legProgress: 0
      });
    }
  }

  /** The miller's round trip: out of the mill, down the path, a look at the river, and back. */
  private buildMiller(renderer: CharacterRenderer, mill: Placed): Walker {
    const rotation = mill.rotation || 0;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);

    const route = [
      { dx: -0.5, dz: 0.0 }, // inside the mill
      { dx: -0.5, dz: 3.0 }, // just inside the front door
      { dx: -0.5, dz: 5.5 }, // out on the pathway
      { dx: 1.8, dz: 5.5 } //  at the river's edge
    ];

    const waypoints = route.map(
      (stop) =>
        new THREE.Vector3(
          (mill.x + stop.dx * cos - stop.dz * sin) * VOXEL_SIZE,
          2 * VOXEL_SIZE + FOOT_LIFT,
          (mill.z + stop.dx * sin + stop.dz * cos) * VOXEL_SIZE
        )
    );

    renderer.group.position.copy(waypoints[0]!);
    renderer.group.lookAt(waypoints[1]!);

    return {
      type: "miller",
      renderer,
      speed: 1.2,
      // He starts the day indoors, so the first thing anyone sees is him coming out.
      paused: true,
      pauseFor: 30,
      progress: 0,
      direction: 1,
      waypoints,
      legIndex: 0,
      legProgress: 0
    };
  }

  public update(time: number, delta: number): void {
    for (const walker of this.walkers) {
      if (walker.waypoints) this.updateRoute(walker, delta);
      else this.updateShuttle(walker, delta);

      if (!walker.paused) {
        walker.renderer.group.position.y += Math.sin(time * 0.01) * 0.05;
      }
      if (walker.type === "villager") this.rideBridge(walker, time);

      walker.renderer.update(time, delta, !walker.paused);
    }
  }

  /** A there-and-back walker: cross, wait, turn around. */
  private updateShuttle(walker: Walker, delta: number): void {
    if (!walker.start || !walker.end) return;

    if (walker.paused) {
      walker.pauseFor -= delta;
      if (walker.pauseFor <= 0) {
        walker.paused = false;
        walker.direction *= -1;
        walker.renderer.group.lookAt(walker.direction > 0 ? walker.end : walker.start);
      }
      return;
    }

    const span = walker.start.distanceTo(walker.end);
    walker.progress += ((walker.speed * delta) / span) * walker.direction;

    if (walker.progress >= 1 || walker.progress <= 0) {
      walker.progress = Math.min(1, Math.max(0, walker.progress));
      walker.paused = true;
      walker.pauseFor = 2;
    }

    walker.renderer.group.position.lerpVectors(walker.start, walker.end, walker.progress);
  }

  /** The miller's route: walk a leg, stop at the end stops, then reverse. */
  private updateRoute(walker: Walker, delta: number): void {
    const stops = walker.waypoints!;

    if (walker.paused) {
      walker.pauseFor -= delta;
      if (walker.pauseFor > 0) return;
      walker.paused = false;
      // A pause at either end of the route is where he turns around.
      if (walker.legIndex === stops.length - 1 && walker.direction === 1) walker.direction = -1;
      else if (walker.legIndex === 0 && walker.direction === -1) walker.direction = 1;
      const next = stops[walker.legIndex + walker.direction];
      if (next) walker.renderer.group.lookAt(next);
      return;
    }

    const from = stops[walker.legIndex]!;
    const to = stops[walker.legIndex + walker.direction];
    if (!to) return;

    walker.legProgress += (walker.speed * delta) / from.distanceTo(to);

    if (walker.legProgress < 1) {
      walker.renderer.group.position.lerpVectors(from, to, walker.legProgress);
      return;
    }

    walker.legProgress = 0;
    walker.legIndex += walker.direction;
    walker.renderer.group.position.copy(stops[walker.legIndex]!);

    if (walker.legIndex === stops.length - 1) {
      walker.paused = true;
      walker.pauseFor = 4; // a moment at the water
    } else if (walker.legIndex === 0) {
      walker.paused = true;
      walker.pauseFor = 30; // back to work inside
    } else {
      const next = stops[walker.legIndex + walker.direction];
      if (next) walker.renderer.group.lookAt(next);
    }
  }

  /** Lifts the bridge walker onto the arch, easing back to ground height at either bank. */
  private rideBridge(walker: Walker, time: number): void {
    if (!this.bridge) return;

    const halfLength = 5 * VOXEL_SIZE;
    const offset = walker.renderer.group.position.x - this.bridge.x * VOXEL_SIZE;
    if (Math.abs(offset) > halfLength) return;

    const along = offset / halfLength;
    const arch = (Math.round((1 - along * along) * 2.2 * 10) / 10) * VOXEL_SIZE;
    const onBridge = this.bridge.y * VOXEL_SIZE + arch + 1.15 * VOXEL_SIZE;
    const onBank = 2.5 * VOXEL_SIZE + 0.05;
    const bob = walker.paused ? 0 : Math.sin(time * 0.01) * 0.05;

    // Weight runs 0 at the crown to 1 at the banks, so the arch meets the ground cleanly.
    walker.renderer.group.position.y = THREE.MathUtils.lerp(onBridge, onBank, along * along) + bob;
  }

  public destroy(scene: THREE.Scene): void {
    for (const walker of this.walkers) {
      scene.remove(walker.renderer.group);
      walker.renderer.dispose();
    }
    this.walkers = [];
  }
}
